import type {ReactElement} from 'react';
import {useEffect, useState, useRef} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Pill, PILL_CONTENT_TYPE, PILL_SIZE} from '@Components/pill-legacy';
import {useIsMobileWidth} from '@Hooks/useIsMobileWidth';
import {Swiper} from '@Components/swiper';
import type {MediaItemData} from '@Components/simple-media-carousel/simple-media-carousel.types';
import {SimpleMediaCarouselItem} from '@Components/simple-media-carousel/components/simple-media-carousel-item/simple-media-carousel-item';
import styles from './simple-media-carousel.module.scss';
import type {DesignSelection} from '../social-media/social-media.types';

export interface SimpleMediaCarouselProps {
  mediaItems: MediaItemData[];
  buttonClasses?: string;
  containerClasses?: string;
  carouselIndicatorClasses?: string;
  counterClasses?: string;
  onIndividualVideoLoad?: (externalVideoDesign: DesignSelection) => void;
  onIndividualImageLoad?: (externalImageDesign: DesignSelection) => void;
  onIndividualAssetErrored?: (externalDesing: DesignSelection) => void;
  doRetryOnIndividualAssetError?: VoidFunction;
  onAllMediaItemsLoaded?: VoidFunction;
  onNext?: VoidFunction;
  onPrev?: VoidFunction;
}

export function SimpleMediaCarousel({...props}: SimpleMediaCarouselProps): ReactElement<any> {
  const scrollableMediaContainerRef = useRef<HTMLDivElement>(null);
  const loadedItems = useRef(0);
  const isScrolling = useRef(false);
  const mediaRefs = useRef<(HTMLVideoElement | HTMLImageElement)[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const numMediaItems = props.mediaItems.length;
  const isMobileScreen = useIsMobileWidth();

  useEffect(() => {
    scrollableMediaContainerRef.current?.addEventListener('scroll', window.PMW.debounce(updateIsScrolling, 100) as VoidFunction, {passive: true});

    return (): void => {
      scrollableMediaContainerRef.current?.removeEventListener('scroll', updateIsScrolling);
    };
  }, []);

  const onItemLoaded = (): void => {
    loadedItems.current += 1;
    if (loadedItems.current !== numMediaItems) {
      return;
    }

    props.onAllMediaItemsLoaded?.();
  };

  const onItemMounted = (el: HTMLVideoElement | HTMLImageElement, index: number): void => {
    mediaRefs.current[index] = el;
  };

  const scrollBy = (offset: number): void => {
    if (!scrollableMediaContainerRef.current) {
      return;
    }

    isScrolling.current = true;
    scrollableMediaContainerRef.current.scrollBy({left: offset, behavior: 'smooth'});
  };

  const updateIsScrolling = (): void => {
    isScrolling.current = false;
  };

  const pauseAllVideos = (): void => {
    mediaRefs.current.forEach((media) => {
      if (media instanceof HTMLVideoElement && !media.paused) {
        media.pause();
      }
    });
  };

  const playVideoIfVisible = (index: number): void => {
    const media = mediaRefs.current[index];
    if (media instanceof HTMLVideoElement && props.mediaItems[index].isVideo) {
      void media.play();
    }
  };

  const onNext = (): void => {
    if (!scrollableMediaContainerRef.current || isScrolling.current || currentIndex === numMediaItems - 1) {
      return;
    }

    pauseAllVideos();
    scrollBy(scrollableMediaContainerRef.current.clientWidth);
    setCurrentIndex((prevIndex) => {
      const newIndex = prevIndex + 1;
      playVideoIfVisible(newIndex);

      return newIndex;
    });

    props.onNext?.();
  };

  const onPrev = (): void => {
    if (!scrollableMediaContainerRef.current || isScrolling.current || currentIndex === 0) {
      return;
    }

    pauseAllVideos();
    scrollBy(-scrollableMediaContainerRef.current.clientWidth);
    setCurrentIndex((prevIndex) => {
      const newIndex = prevIndex - 1;
      playVideoIfVisible(newIndex);

      return newIndex;
    });

    props.onPrev?.();
  };

  const getButtons = (): ReactElement<any> => {
    return (
      <div className={isMobileScreen ? '_hidden' : `flex-justify-between ${styles.carouselButtonContainer}`}>
        <Icon
          icon="icon-caret-left"
          size={IconSize.SIZE_ICON_16}
          type={IconType.SECONDARY}
          shape={IconShape.CIRCLE}
          className={`${styles.carouselButton} ${currentIndex === 0 ? styles.hidden : ''} ${props.buttonClasses}`}
          onClick={onPrev}
        />
        <Icon
          icon="icon-caret-right"
          size={IconSize.SIZE_ICON_16}
          type={IconType.SECONDARY}
          shape={IconShape.CIRCLE}
          className={`${styles.carouselButton} ${currentIndex === numMediaItems - 1 ? styles.hidden : ''} ${props.buttonClasses}`}
          onClick={onNext}
        />
      </div>
    );
  };

  const getMediaItems = (): ReactElement<any>[] => {
    const mediaItems: ReactElement<any>[] = [];

    for (let i = 0; i < numMediaItems; i++) {
      mediaItems.push(
        <SimpleMediaCarouselItem
          mediaItem={props.mediaItems[i]}
          index={i}
          key={`carousel-item-${i}`}
          onItemMounted={onItemMounted}
          onItemLoaded={onItemLoaded}
          onIndividualImageLoad={props.onIndividualImageLoad}
          onIndividualVideoLoad={props.onIndividualVideoLoad}
          onIndividualAssetErrored={props.onIndividualAssetErrored}
          doRetryOnIndividualAssetError={props.doRetryOnIndividualAssetError}
        />
      );
    }

    return mediaItems;
  };

  const getCounter = (): ReactElement<any> => {
    return (
      <Pill
        content={{
          type: PILL_CONTENT_TYPE.TEXT,
          text: `${currentIndex + 1}/${numMediaItems}`,
          textColor: 'neutral-70',
          textClasses: 'body-sm-regular',
        }}
        size={PILL_SIZE.SMALL}
        className={`${styles.carouselCounterPill} ${props.counterClasses}`}
      />
    );
  };

  const isLeftMostButNotFirstIndicator = (index: number, startIndex: number): boolean => {
    return index === startIndex && startIndex > 0;
  };

  const isRightMostButNotLastIndicator = (index: number, endIndex: number): boolean => {
    return index === endIndex && endIndex < numMediaItems - 1;
  };

  const getMultiPostCarouselIndicators = (): ReactElement<any> => {
    const maxVisibleIndicators = 5;
    const centerIndicator = Math.floor(maxVisibleIndicators / 2);

    let startIndex = Math.max(0, currentIndex - centerIndicator);
    const endIndex = Math.min(numMediaItems - 1, startIndex + maxVisibleIndicators - 1);

    if (endIndex - startIndex < maxVisibleIndicators - 1) {
      startIndex = Math.max(0, endIndex - maxVisibleIndicators + 1);
    }

    const circles = [];
    for (let i = startIndex; i <= endIndex; i++) {
      const isSmallIndicator = isLeftMostButNotFirstIndicator(i, startIndex) || isRightMostButNotLastIndicator(i, endIndex);

      circles.push(
        <div
          key={`carousel-circle-indicator-${i}`}
          className={`
          ${styles.carouselIndicator}
          ${i === currentIndex ? styles.selected : ''}
          ${isSmallIndicator ? styles.smallIndicator : ''}
        `}
        />
      );
    }

    return <div className={`flex-center ${styles.carouselIndicatorContainer} ${props.carouselIndicatorClasses}`}>{circles}</div>;
  };

  const getCarousel = (): ReactElement<any> => {
    return (
      <div className={`border-s-standard radius-4 flexbox ${styles.carouselContainer} ${props.containerClasses}`}>
        {getButtons()}
        <div className={`flexbox ${styles.scrollableMediaContainer}`} ref={scrollableMediaContainerRef}>
          {getMediaItems()}
        </div>
        {getCounter()}
        {getMultiPostCarouselIndicators()}
      </div>
    );
  };

  const getSwiperForMobile = (): ReactElement<any> => {
    return (
      <div className="flexbox _full-height _full-width">
        <Swiper onSwipeLeft={onNext} onSwipeRight={onPrev}>
          {getCarousel()}
        </Swiper>
      </div>
    );
  };

  return isMobileScreen ? getSwiperForMobile() : getCarousel();
}
